import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import * as TooltipPrimitive from '@radix-ui/react-tooltip';
import './App.css';

// Shared Components
import Login from './shared/components/Login';
import DemoLogin from './shared/components/DemoLogin';
import PlatformBridge from './shared/components/PlatformBridge';
import AppLayout from './shared/components/AppLayout';
import HelpCenter from './shared/components/HelpCenter';
import ProfilePage from './shared/components/account/ProfilePage';
import ForcedPasswordChangePage from './shared/components/account/ForcedPasswordChangePage';
import ActivatePage from './shared/components/account/ActivatePage';
import ResetPasswordPage from './shared/components/account/ResetPasswordPage';
import apiConfig from './config/apiConfig';
import { FORCED_CHANGE_PATH, isPasswordChangeRequired } from './shared/components/account/passwordChangeRequired';

// Foundation Module Pages
import CoordinatorDashboard from './modules/foundations/pages/CoordinatorDashboard';
import PersonaAssessmentFlow from './modules/foundations/pages/PersonaAssessmentFlow';
import PersonaAccess from './modules/foundations/pages/PersonaAccess';
import AssessmentDashboard from './modules/foundations/pages/AssessmentDashboard';
import PortfolioDashboard from './modules/foundations/pages/PortfolioDashboard';
import PortfolioGroupsManager from './components/PortfolioGroupsManager';
import AdminMoved from './components/AdminMoved';

// Compass Module
import CompassModule from './modules/compass/CompassModule';

// Defense Module
import DefenseModule from './modules/defense/DefenseModule';
import { RouteSessionGate } from './shared/components/progress/AssessmentProgressGate';

// Protected Route Component (checks both sessionStorage and localStorage for demo viewer support)
const ProtectedRoute = ({ children, allowedRoles = ['coordinator'] }) => {
  const isAuthenticated = sessionStorage.getItem('isAuthenticated') || localStorage.getItem('isAuthenticated');
  const userRole = sessionStorage.getItem('userRole') || localStorage.getItem('userRole');

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  if (!allowedRoles.includes(userRole)) {
    return <Navigate to="/login" replace />;
  }

  // A superadmin-issued password must be replaced first; the server refuses everything else meanwhile.
  if (isPasswordChangeRequired()) {
    return <Navigate to={FORCED_CHANGE_PATH} replace />;
  }

  return children;
};

// SuperadminRoute — gates on the is_superadmin flag set during coordinator
// login. Mirrors PRISM: superadmin is a coordinator attribute, not a role.
const SuperadminRoute = ({ children }) => {
  const isAuthenticated = localStorage.getItem('isAuthenticated');
  const isSuperadmin = localStorage.getItem('isSuperadmin') === 'true';
  if (!isAuthenticated) return <Navigate to="/login" replace />;
  if (isPasswordChangeRequired()) return <Navigate to={FORCED_CHANGE_PATH} replace />;
  if (!isSuperadmin) return <Navigate to="/coordinator-dashboard" replace />;
  return children;
};

// PersonaProtectedRoute — used by /persona-assessment/:sessionId when the URL
// carries ?locked=1. Validates that a persona JWT exists; if not, kicks back
// to the magic-link landing (which will show an error since there's no token).
const PersonaProtectedRoute = ({ children }) => {
  const personaToken = localStorage.getItem('foundations_persona_token');
  if (!personaToken) {
    return <Navigate to="/persona/access" replace />;
  }
  return children;
};

// PersonaShell — minimal frame for locked-persona assessment view. Drops the
// coordinator-shaped sidebar (UnifiedNavigation) that AppLayout renders;
// personas should see only their own assessment, not coordinator nav.
const PersonaShell = ({ children }) => (
  <div className="min-h-screen" style={{ background: 'var(--ra-navy)' }}>
    <a
      href="#main-content"
      className="sr-only focus:not-sr-only focus:absolute focus:z-[9999] focus:top-4 focus:left-4 focus:px-4 focus:py-2 focus:rounded-lg focus:text-white focus:font-semibold"
      style={{ background: 'var(--ra-gradient-cta)' }}
    >
      Skip to main content
    </a>
    <main id="main-content" className="min-h-screen overflow-auto">
      {children}
    </main>
  </div>
);

// SmartAssessmentRoute — picks the right protector + shell based on URL.
// Locked-persona URL → persona JWT + minimal shell. Otherwise → coordinator/PM auth + AppLayout.
const SmartAssessmentRoute = ({ children }) => {
  const isLocked = typeof window !== 'undefined' && /[?&]locked=1\b/.test(window.location.search || '');
  if (isLocked) {
    return (
      <PersonaProtectedRoute>
        <PersonaShell>{children}</PersonaShell>
      </PersonaProtectedRoute>
    );
  }
  return (
    <ProtectedRoute allowedRoles={['coordinator', 'portfolio_manager']}>
      <AppLayout>{children}</AppLayout>
    </ProtectedRoute>
  );
};

function App() {
  return (
    <TooltipPrimitive.Provider delayDuration={300}>
    <Router>
      <Routes>
        {/* Authentication */}
        <Route path="/login" element={<Login />} />
        <Route path="/demo" element={<DemoLogin />} />
        <Route path="/auth/platform-bridge" element={<PlatformBridge />} />
        {/* Forced replacement of a superadmin-issued password. Guards itself: needs a sign-in, and sends
            anyone whose password is not temporary on to their home screen. */}
        <Route path={FORCED_CHANGE_PATH} element={<ForcedPasswordChangePage apiBaseUrl={apiConfig.getBaseUrl()} />} />
        {/* Public activation page for new coordinators arriving from email link */}
        <Route path="/activate" element={<ActivatePage apiBaseUrl={apiConfig.getBaseUrl()} />} />
        {/* Public page for a staff-sent password reset link (piece 3) */}
        <Route path="/reset-password" element={<ResetPasswordPage apiBaseUrl={apiConfig.getBaseUrl()} />} />
        <Route path="/persona/access" element={<PersonaAccess />} />
        
        {/* Foundation Module Routes */}
        <Route path="/coordinator-dashboard" element={
          <ProtectedRoute allowedRoles={['coordinator', 'demo_viewer']}>
            <AppLayout>
              <CoordinatorDashboard />
            </AppLayout>
          </ProtectedRoute>
        } />
        
        <Route path="/portfolio-dashboard" element={
          <ProtectedRoute allowedRoles={['portfolio_manager']}>
            <AppLayout>
              <PortfolioDashboard />
            </AppLayout>
          </ProtectedRoute>
        } />
        
        <Route path="/portfolio-groups" element={
          <ProtectedRoute allowedRoles={['portfolio_manager']}>
            <AppLayout>
              <PortfolioGroupsManager />
            </AppLayout>
          </ProtectedRoute>
        } />
        
        {/* Self-service profile + password change. Coordinators only (superadmins are
            coordinators); the endpoint refuses demo viewers and impersonation sessions. */}
        <Route path="/profile" element={
          <ProtectedRoute allowedRoles={['coordinator']}>
            <AppLayout>
              <ProfilePage />
            </AppLayout>
          </ProtectedRoute>
        } />

        {/* F9: the admin console moved to Platform Oversight; /admin now links there. */}
        <Route path="/admin" element={
          <SuperadminRoute>
            <AdminMoved />
          </SuperadminRoute>
        } />
        
        <Route path="/persona-assessment/:sessionId" element={
          <SmartAssessmentRoute>
            <PersonaAssessmentFlow />
          </SmartAssessmentRoute>
        } />
        
        <Route path="/assessment/:sessionId/results" element={
          <ProtectedRoute allowedRoles={['coordinator', 'portfolio_manager', 'demo_viewer']}>
            <AppLayout>
              <RouteSessionGate source="param"><AssessmentDashboard /></RouteSessionGate>
            </AppLayout>
          </ProtectedRoute>
        } />

        {/* Compass Module Routes - Executive Intelligence */}
        <Route path="/compass/*" element={
          <ProtectedRoute allowedRoles={['coordinator', 'portfolio_manager', 'admin', 'demo_viewer']}>
            <AppLayout currentApp="compass">
              <RouteSessionGate source="localStorage"><CompassModule /></RouteSessionGate>
            </AppLayout>
          </ProtectedRoute>
        } />

        <Route path="/intelligence/*" element={
          <ProtectedRoute allowedRoles={['coordinator', 'portfolio_manager', 'admin', 'demo_viewer']}>
            <AppLayout currentApp="compass">
              <RouteSessionGate source="localStorage"><CompassModule /></RouteSessionGate>
            </AppLayout>
          </ProtectedRoute>
        } />

        {/* Defense Module Routes - Risk Studio */}
        <Route path="/defense/*" element={
          <ProtectedRoute allowedRoles={['coordinator', 'portfolio_manager', 'admin', 'demo_viewer']}>
            <AppLayout currentApp="defense">
              <RouteSessionGate source="localStorage"><DefenseModule /></RouteSessionGate>
            </AppLayout>
          </ProtectedRoute>
        } />

        <Route path="/studio/*" element={
          <ProtectedRoute allowedRoles={['coordinator', 'portfolio_manager', 'admin', 'demo_viewer']}>
            <AppLayout currentApp="defense">
              <RouteSessionGate source="localStorage"><DefenseModule /></RouteSessionGate>
            </AppLayout>
          </ProtectedRoute>
        } />
        
        {/* Help Center — methodology reference, accessible to all authenticated roles */}
        <Route path="/help" element={
          <ProtectedRoute allowedRoles={['coordinator', 'portfolio_manager', 'admin', 'demo_viewer']}>
            <AppLayout>
              <HelpCenter />
            </AppLayout>
          </ProtectedRoute>
        } />

        {/* Default Routes */}
        <Route path="/" element={<Navigate to="/login" replace />} />
        <Route path="*" element={<Navigate to="/coordinator-dashboard" replace />} />
      </Routes>
    </Router>
    </TooltipPrimitive.Provider>
  );
}

export default App;